import {
  App,
  Button,
  Card,
  Col,
  Empty,
  Row,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  EditOutlined,
  ExportOutlined,
  MailOutlined,
  OrderedListOutlined,
  PhoneOutlined,
  PlusOutlined,
  StopOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiClient } from "../../../services/api/apiClient";
import { formatPhone, phoneToTelHref } from "../../../utils/phone";
import { ContactProposalModal, type ProposalKind } from "./ContactProposalModal";

const { Text } = Typography;

interface ContactRoleEntry {
  id: string;
  producer_contact: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  position: number | null;
  position_name: string | null;
  target: "license" | "farm";
  farm: number | null;
  priority: number;
}

interface FarmChain {
  farm_id: number;
  farm_name: string;
  contacts: ContactRoleEntry[];
}

interface ChainsResponse {
  license: ContactRoleEntry[];
  farms: FarmChain[];
}

/** Open ticket from the "Contactos" category that's still pending
 *  validation by the admin. Parsed out of the ticket's structured
 *  `data-proposal` JSON by the backend. */
interface PendingProposal {
  ticket_id: number;
  ticket_code: string;
  kind: "new" | "edit" | "remove";
  contact_id: string | null;
  contact_name: string | null;
  farm_id: number | null;
  proposed?: {
    name?: string;
    phone?: string;
    email?: string;
    position?: string;
  } | null;
  reason?: string | null;
  created: string;
  created_by: string | null;
}

interface ContactChainsPanelProps {
  auditId: number;
  auditReference: string;
  producerId: number;
  producerName: string;
  farmId: number | null;
  farmName: string | null;
  /** When true (admin), the panel shows a "Editar contactos" button to
   *  the full editor and hides the propose-X actions. When false
   *  (auditor / coordinator), proposals are the only available actions. */
  isAdmin: boolean;
  /** When true, the contact-card grid uses larger breakpoints so each
   *  card takes more relative width — meant for narrow side columns
   *  (e.g. when the panel lives in a 8/24 column). Default false. */
  compact?: boolean;
  /** Layout for the cards. `grid` (default) uses a responsive Row/Col
   *  grid; `horizontal` lays the cards in a single row with
   *  horizontal scroll — useful above the map where we want a fixed
   *  height regardless of the contact count. */
  layout?: "grid" | "horizontal";
  /** When true, hide / disable every "propose" action (new contact,
   *  edit, remove) and the admin "Editar contactos" link. Used when
   *  the parent audit is finalised — no more contact mutations can
   *  happen against that audit. The list of existing contacts stays
   *  visible (read-only). */
  readOnly?: boolean;
}

/**
 * Read-only view of the producer's contact chains relevant to this
 * audit (license + audit's farm). Non-admin users get "Propose ..."
 * buttons that file a Contactos ticket; admins get a link to the full
 * editor where they can apply the change themselves.
 */
export const ContactChainsPanel = ({
  auditId,
  auditReference,
  producerId,
  producerName,
  farmId,
  farmName,
  isAdmin,
  compact = false,
  layout = "grid",
  readOnly = false,
}: ContactChainsPanelProps) => {
  const { t } = useTranslation();
  const { message: appMessage } = App.useApp();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ChainsResponse | null>(null);
  const [pending, setPending] = useState<PendingProposal[]>([]);

  const [proposalKind, setProposalKind] = useState<ProposalKind | null>(null);
  const [proposalContact, setProposalContact] =
    useState<ContactRoleEntry | null>(null);
  // The contact's chain scope (license vs a specific farm) determines
  // what we ship in the proposal payload. We capture it when the user
  // clicks the propose button.
  const [proposalScope, setProposalScope] = useState<{
    farmId: number | null;
    farmName: string | null;
  }>({ farmId: null, farmName: null });

  // Reorder mode: when active, contact cards show ↑/↓ instead of the
  // propose actions. Local state holds the reordered contacts until
  // the user confirms — that's when we POST to /contact-roles/bulk-reorder/.
  const [reorderMode, setReorderMode] = useState(false);
  const [reorderDraft, setReorderDraft] = useState<ContactRoleEntry[] | null>(
    null,
  );
  const [savingReorder, setSavingReorder] = useState(false);

  const fetchChains = async () => {
    if (!producerId) return;
    setLoading(true);
    try {
      // The chains endpoint is mounted in the legacy `/api/` namespace,
      // no `/v1/` prefix (see backend/apps/api/urls.py — registered as
      // `producers/<int:producer_id>/contact-chains/`).
      const res = await apiClient.get(
        `/producers/${producerId}/contact-chains/`,
      );
      setData(res.data as ChainsResponse);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchPending = async () => {
    if (!auditId) return;
    try {
      const res = await apiClient.get(
        `/audits/${auditId}/contact-proposals/`,
      );
      setPending(Array.isArray(res.data) ? (res.data as PendingProposal[]) : []);
    } catch {
      setPending([]);
    }
  };

  useEffect(() => {
    fetchChains();
    fetchPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [producerId, auditId]);

  // Index: contact_id → first pending proposal targeting it (edit/remove).
  const pendingByContactId = useMemo(() => {
    const map = new Map<string, PendingProposal>();
    for (const p of pending) {
      if (p.contact_id && !map.has(p.contact_id)) {
        map.set(p.contact_id, p);
      }
    }
    return map;
  }, [pending]);

  // Ghost rows: pending `new` proposals targeting this audit's farm
  // chain. Show after the real contacts.
  const pendingNewForFarm = useMemo(
    () =>
      pending.filter(
        (p) => p.kind === "new" && p.farm_id === farmId,
      ),
    [pending, farmId],
  );

  // Audit's own farm chain (if the producer has it). Other farms are
  // not shown — the auditor is only inspecting this farm right now.
  const auditFarmChain = useMemo(() => {
    if (!data || farmId == null) return null;
    return data.farms.find((f) => f.farm_id === farmId) ?? null;
  }, [data, farmId]);

  const openProposal = (
    kind: ProposalKind,
    contact: ContactRoleEntry | null,
    scopeFarmId: number | null,
    scopeFarmName: string | null,
  ) => {
    setProposalKind(kind);
    setProposalContact(contact);
    setProposalScope({ farmId: scopeFarmId, farmName: scopeFarmName });
  };

  const enterReorderMode = () => {
    setReorderDraft(auditFarmChain?.contacts ?? []);
    setReorderMode(true);
  };

  const cancelReorder = () => {
    setReorderMode(false);
    setReorderDraft(null);
  };

  const moveContact = (id: string, direction: "up" | "down") => {
    setReorderDraft((prev) => {
      if (!prev) return prev;
      const idx = prev.findIndex((c) => c.id === id);
      if (idx === -1) return prev;
      const swapIdx = direction === "up" ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
      return next;
    });
  };

  const saveReorder = async () => {
    if (!reorderDraft || farmId == null) return;
    // Build the payload from the current draft order. Priorities are
    // 1-based and dense (1..N) so the backend can trust them.
    const items = reorderDraft.map((c, idx) => ({
      id: c.id,
      priority: idx + 1,
    }));
    // Skip the request if nothing actually changed — avoids an extra
    // round-trip when the user opens/closes reorder without touching.
    const original = auditFarmChain?.contacts ?? [];
    const unchanged =
      original.length === items.length &&
      original.every((c, idx) => c.id === items[idx].id);
    if (unchanged) {
      cancelReorder();
      return;
    }
    setSavingReorder(true);
    try {
      await apiClient.post(`/contact-roles/bulk-reorder/`, {
        target: "farm",
        farm: farmId,
        items,
      });
      appMessage.success(
        t("contacts.reorder_success", "Orden de contactos actualizado"),
      );
      cancelReorder();
      await fetchChains();
    } catch (err: any) {
      const detail =
        err?.originalError?.response?.data?.detail ||
        err?.message ||
        t("contacts.reorder_error", "No se pudo guardar el orden");
      appMessage.error(detail);
    } finally {
      setSavingReorder(false);
    }
  };

  const renderRow = (
    role: ContactRoleEntry,
    scopeFarmId: number | null,
    scopeFarmName: string | null,
    reorderCtx?: {
      index: number;
      total: number;
    },
  ) => {
    const pendingForRow = pendingByContactId.get(role.producer_contact);
    const pendingTag = pendingForRow ? (
      <Tooltip
        title={t(
          "contacts.pending_tooltip",
          "Pendiente de validar por el administrador ({{code}})",
          { code: pendingForRow.ticket_code },
        )}
      >
        <Tag color="gold" icon={<ClockCircleOutlined />} style={{ margin: 0 }}>
          {pendingForRow.kind === "remove"
            ? t("contacts.pending_remove", "Baja pendiente")
            : t("contacts.pending_edit", "Edición pendiente")}
        </Tag>
      </Tooltip>
    ) : null;
    const actionsDisabled = Boolean(pendingForRow);
    // Show the effective priority: the persisted `priority` when not
    // reordering, or the 1-based draft index while the user drags. This
    // keeps the badge in sync with the visible position.
    const displayPriority = reorderCtx ? reorderCtx.index + 1 : role.priority;

    const reorderActions = reorderCtx
      ? [
          <Tooltip key="up" title={t("contacts.move_up", "Subir")}>
            <Button
              type="text"
              size="small"
              icon={<ArrowUpOutlined />}
              disabled={reorderCtx.index === 0}
              onClick={() => moveContact(role.id, "up")}
            />
          </Tooltip>,
          <Tooltip key="down" title={t("contacts.move_down", "Bajar")}>
            <Button
              type="text"
              size="small"
              icon={<ArrowDownOutlined />}
              disabled={reorderCtx.index === reorderCtx.total - 1}
              onClick={() => moveContact(role.id, "down")}
            />
          </Tooltip>,
        ]
      : undefined;

    return (
        <Card
          key={role.id}
          size="small"
          hoverable={!reorderCtx}
          style={{
            height: "100%",
            opacity: actionsDisabled ? 0.85 : 1,
            borderLeft: reorderCtx
              ? "3px dashed #1677ff"
              : "3px solid #1677ff",
          }}
          styles={{ body: { padding: 12 } }}
          actions={
            // Reorder mode replaces propose actions with ↑/↓ buttons.
            // When the audit is finalised (`readOnly`) we hide the
            // propose-edit / propose-remove actions entirely — the
            // proposal would target a closed audit anyway.
            reorderActions
              ? reorderActions
              : !isAdmin && !readOnly
              ? [
                  <Tooltip
                    key="edit"
                    title={
                      actionsDisabled
                        ? t(
                            "contacts.already_pending",
                            "Ya hay una propuesta pendiente para este contacto",
                          )
                        : t("contacts.propose_edit", "Proponer edición")
                    }
                  >
                    <Button
                      type="text"
                      size="small"
                      icon={<EditOutlined />}
                      disabled={actionsDisabled}
                      onClick={() =>
                        openProposal("edit", role, scopeFarmId, scopeFarmName)
                      }
                    >
                      {t("contacts.action_edit_short", "Editar")}
                    </Button>
                  </Tooltip>,
                  <Tooltip
                    key="remove"
                    title={
                      actionsDisabled
                        ? t(
                            "contacts.already_pending",
                            "Ya hay una propuesta pendiente para este contacto",
                          )
                        : t("contacts.propose_remove", "Proponer baja")
                    }
                  >
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<StopOutlined />}
                      disabled={actionsDisabled}
                      onClick={() =>
                        openProposal("remove", role, scopeFarmId, scopeFarmName)
                      }
                    >
                      {t("contacts.action_remove_short", "Baja")}
                    </Button>
                  </Tooltip>,
                ]
              : undefined
          }
        >
          {/* Header: priority + name */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <Tag
              color={reorderCtx ? "blue" : "default"}
              style={{ minWidth: 28, textAlign: "center", margin: 0 }}
            >
              #{displayPriority}
            </Tag>
            <strong style={{ fontSize: 14, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {role.contact_name || "—"}
            </strong>
          </div>
          {role.position_name && (
            <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
              {role.position_name}
            </Text>
          )}
          {pendingTag && <div style={{ marginBottom: 8 }}>{pendingTag}</div>}
          {/* Contact links — always render both rows even when a value
              is missing so every card in the row stays the same height.
              An empty row shows muted placeholder text ("Sin teléfono"
              / "Sin email") instead of collapsing. */}
          <div style={{ fontSize: 13 }}>
            {role.contact_phone ? (
              <a href={`tel:${phoneToTelHref(role.contact_phone)}`}>
                <PhoneOutlined style={{ marginRight: 6 }} />
                {formatPhone(role.contact_phone)}
              </a>
            ) : (
              <Text type="secondary">
                <PhoneOutlined style={{ marginRight: 6 }} />
                {t("contacts.no_phone", "Sin teléfono")}
              </Text>
            )}
          </div>
          <div
            style={{
              fontSize: 13,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {role.contact_email ? (
              <a
                href={`mailto:${role.contact_email}`}
                title={role.contact_email}
              >
                <MailOutlined style={{ marginRight: 6 }} />
                {role.contact_email}
              </a>
            ) : (
              <Text type="secondary">
                <MailOutlined style={{ marginRight: 6 }} />
                {t("contacts.no_email", "Sin email")}
              </Text>
            )}
          </div>
        </Card>
    );
  };

  /** Ghost card representing a pending `kind=new` proposal. Same
   *  shape as a real contact card but tinted gold to distinguish. */
  const renderPendingNewRow = (p: PendingProposal) => (
      <Card
        key={`pending-new-${p.ticket_id}`}
        size="small"
        style={{
          height: "100%",
          background: "#fffbe6",
          borderLeft: "3px dashed #faad14",
        }}
        styles={{ body: { padding: 12 } }}
      >
        <div style={{ marginBottom: 4 }}>
          <Tag color="gold" icon={<ClockCircleOutlined />} style={{ margin: 0 }}>
            {t("contacts.pending_new", "Alta pendiente")}
          </Tag>
        </div>
        <strong style={{ fontSize: 14 }}>{p.proposed?.name || "—"}</strong>
        {p.proposed?.position && (
          <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
            {p.proposed.position}
          </Text>
        )}
        {p.proposed?.phone && (
          <div style={{ fontSize: 13 }}>
            <a href={`tel:${p.proposed.phone}`}>
              <PhoneOutlined style={{ marginRight: 6 }} />
              {p.proposed.phone}
            </a>
          </div>
        )}
        {p.proposed?.email && (
          <div style={{ fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            <a href={`mailto:${p.proposed.email}`} title={p.proposed.email}>
              <MailOutlined style={{ marginRight: 6 }} />
              {p.proposed.email}
            </a>
          </div>
        )}
        <Tooltip
          title={t(
            "contacts.pending_tooltip",
            "Pendiente de validar por el administrador ({{code}})",
            { code: p.ticket_code },
          )}
        >
          <Tag color="default" style={{ margin: "8px 0 0", fontSize: 11 }}>
            {p.ticket_code}
          </Tag>
        </Tooltip>
      </Card>
  );

  const renderChainBlock = (
    title: string,
    color: string,
    contacts: ContactRoleEntry[],
    scopeFarmId: number | null,
    scopeFarmName: string | null,
  ) => (
    <div style={{ marginBottom: 14 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 6,
        }}
      >
        <Space size={6}>
          <Tag color={color}>{title}</Tag>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {contacts.length} {t("contacts.contacts_n", "contacto(s)")}
          </Text>
        </Space>
      </div>
      {contacts.length === 0 && pendingNewForFarm.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t("contacts.empty_chain", "Sin contactos en esta cadena")}
          style={{ margin: "8px 0" }}
        />
      ) : layout === "horizontal" ? (
        // Single-row horizontal scroll. Each card has a fixed width
        // so they line up evenly regardless of the contact count.
        <div
          style={{
            display: "flex",
            gap: 12,
            overflowX: "auto",
            overflowY: "hidden",
            paddingBottom: 4,
          }}
        >
          {contacts.map((c, idx) => (
            <div key={c.id} style={{ minWidth: 260, maxWidth: 260, flexShrink: 0 }}>
              {renderRow(
                c,
                scopeFarmId,
                scopeFarmName,
                reorderMode
                  ? { index: idx, total: contacts.length }
                  : undefined,
              )}
            </div>
          ))}
          {!reorderMode &&
            pendingNewForFarm.map((p) => (
              <div key={`pending-${p.ticket_id}`} style={{ minWidth: 260, maxWidth: 260, flexShrink: 0 }}>
                {renderPendingNewRow(p)}
              </div>
            ))}
        </div>
      ) : (
        <Row gutter={[12, 12]}>
          {contacts.map((c, idx) => (
            <Col
              {...(compact ? { xs: 24 } : { xs: 24, sm: 12, md: 8, xl: 6 })}
              key={c.id}
            >
              {renderRow(
                c,
                scopeFarmId,
                scopeFarmName,
                reorderMode
                  ? { index: idx, total: contacts.length }
                  : undefined,
              )}
            </Col>
          ))}
          {!reorderMode &&
            pendingNewForFarm.map((p) => (
              <Col
                {...(compact ? { xs: 24 } : { xs: 24, sm: 12, md: 8, xl: 6 })}
                key={`pending-${p.ticket_id}`}
              >
                {renderPendingNewRow(p)}
              </Col>
            ))}
        </Row>
      )}
    </div>
  );

  return (
    <Card
      title={
        <Space>
          <TeamOutlined />
          <span>{t("contacts.audit_contacts_title", "Contactos del productor")}</span>
        </Space>
      }
      bordered={false}
      style={{ marginBottom: 24 }}
      extra={
        // Reorder mode swaps the CTA cluster for Save/Cancel while
        // the user is dragging cards around.
        reorderMode ? (
          <Space>
            <Button
              icon={<CloseOutlined />}
              onClick={cancelReorder}
              disabled={savingReorder}
            >
              {t("common.cancel", "Cancelar")}
            </Button>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={saveReorder}
              loading={savingReorder}
            >
              {t("contacts.reorder_save", "Guardar orden")}
            </Button>
          </Space>
        ) : // Finalised audits: hide every CTA (admin link + propose).
        // The contact data underneath stays visible so the report
        // can still be cross-checked.
        readOnly ? null : (
          <Space>
            {/* Reorder button — auditor / coord / admin can all sort
                the farm-chain contacts. Only enabled when there are at
                least two contacts to swap. */}
            {farmId != null && (auditFarmChain?.contacts.length ?? 0) > 1 ? (
              <Button
                icon={<OrderedListOutlined />}
                onClick={enterReorderMode}
              >
                {t("contacts.reorder", "Reordenar")}
              </Button>
            ) : null}
            {isAdmin && producerId ? (
              <Button
                type="link"
                icon={<ExportOutlined />}
                href={`/producers/show/${producerId}`}
                target="_blank"
                rel="noreferrer"
              >
                {t("contacts.edit_full", "Editar contactos del productor")}
              </Button>
            ) : !isAdmin ? (
              // Non-admin: dispatcher CTA. Defaults the proposal to the
              // audit's farm chain (more specific) when there is one;
              // otherwise to the license chain. Each chain block keeps its
              // own scoped button below for finer-grained scope.
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() =>
                  openProposal(
                    "new",
                    null,
                    farmId ?? null,
                    farmId ? (farmName ?? null) : null,
                  )
                }
              >
                {t("contacts.propose_new_long", "Proponer nuevo contacto")}
              </Button>
            ) : null}
          </Space>
        )
      }
    >
      {loading ? (
        <div style={{ padding: 24, textAlign: "center" }}>
          <Spin />
        </div>
      ) : !data ? (
        <Empty
          description={t(
            "contacts.load_error_short",
            "No se pudieron cargar los contactos",
          )}
        />
      ) : (
        renderChainBlock(
          t("contacts.chain_farm", "Cadena de la finca") +
            (farmName ? ` · ${farmName}` : ""),
          "blue",
          reorderMode
            ? (reorderDraft ?? [])
            : (auditFarmChain?.contacts ?? []),
          farmId,
          farmName,
        )
      )}

      <ContactProposalModal
        visible={proposalKind != null}
        kind={proposalKind ?? "edit"}
        auditId={auditId}
        auditReference={auditReference}
        producerId={producerId}
        producerName={producerName}
        farmId={proposalScope.farmId}
        farmName={proposalScope.farmName}
        contact={proposalContact}
        onClose={() => {
          setProposalKind(null);
          setProposalContact(null);
        }}
        onSubmitted={fetchPending}
      />
    </Card>
  );
};

export default ContactChainsPanel;
